import React from 'react';
import styled from 'styled-components';
import { Popover } from 'components';

interface OfficesPopoverCellProps {
  oids: string[];
  officeNames: string[];
}

const POPOVER_MAX_HEIGHT = 234;

const OfficesPopoverCell: React.FC<OfficesPopoverCellProps> = ({
  oids,
  officeNames,
}) => {
  const [open, setOpen] = React.useState(false);

  const offices = oids.map((oid, index) => ({
    oid,
    name: officeNames?.[index] || '',
  }));

  if (offices.length === 0) return <span>-</span>;

  if (offices.length === 1) {
    const office = offices[0];
    return (
      <span>
        {office.name ? `${office.name} (${office.oid})` : office.oid}
      </span>
    );
  }

  const displayText = offices
    .map(o => (o.name ? `${o.name} (${o.oid})` : o.oid))
    .join(', ');

  const popoverContent = (
    <PopoverContainer>
      <PopoverHeader>
        <HeaderTitle>Offices</HeaderTitle>
        <CloseButton onClick={() => setOpen(false)}>×</CloseButton>
      </PopoverHeader>
      <OfficesList>
        {offices.map((office, index) => (
          <OfficeItem key={`${office.oid}-${index}`}>
            <OfficeName>{office.name || office.oid}</OfficeName>
            <OfficeOid>OID: {office.oid}</OfficeOid>
          </OfficeItem>
        ))}
      </OfficesList>
    </PopoverContainer>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      content={popoverContent}
      placement="bottomLeft"
      overlayStyle={{ width: 302 }}
      overlayInnerStyle={{ padding: 0, borderRadius: 8 }}
    >
      <CellWrapper $isOpen={open} onClick={() => setOpen(!open)}>
        <TruncatedText>{displayText}</TruncatedText>
      </CellWrapper>
    </Popover>
  );
};

const CellWrapper = styled.div<{ $isOpen: boolean }>`
  cursor: pointer;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 4px;
  background: ${({ $isOpen }) => ($isOpen ? '#F5F5F5' : 'transparent')};

  &:hover {
    background: ${({ $isOpen }) => ($isOpen ? '#F5F5F5' : '#FAFAFA')};
  }
`;

const TruncatedText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const PopoverContainer = styled.div`
  max-height: ${POPOVER_MAX_HEIGHT}px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 8px;
  background: #fff;
  flex-shrink: 0;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const CloseButton = styled.span`
  cursor: pointer;
  font-size: 18px;
  color: #999;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const OfficesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 8px;
`;

const OfficeItem = styled.div`
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const OfficeName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const OfficeOid = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  margin-top: 2px;
`;

export default OfficesPopoverCell;
